import { create } from "zustand";
import {
  filter,
  samplingControlReport,
  samplingLIDReport,
  samplingReport,
} from "./helpers";
import { errorAlert, successAlert } from "../../../utils/alerts";
import {
  postData,
  getData,
  putData,
  putDataFile,
  getDataFile,
} from "@services/utils/formHelpers";
import {
  GenericOptionsType,
  IFormNewReport,
  IFormReportInputs,
  LidForm,
  PayloadAnexos,
  ReportType,
  ServiceReport,
  ControlReportResult,
} from "@app/types/report.types";
import dayjs from "dayjs";
import { ResponseService } from "../components/formLid";

type UseReport = {
  reportList: ServiceReport[];
  get: (id: ServiceReport["id"]) => ServiceReport | undefined;
  save: (report: IFormNewReport, silent?: boolean) => Promise<boolean>;
  remove: (id: ReportType["id"]) => Promise<void>;
  fetchAll: () => void;
  loading: boolean;
  loadingEdit: boolean;
  loadingSave: boolean;
  loadingDownload: boolean;
  loadingSendEmail: boolean;
  referenceList: GenericOptionsType[];
  downloadReport: (
    id: ReportType["id"],
    refReport?: string,
    resultsConsecutive?: number,
    fechaEntrega?: string
  ) => Promise<boolean>;
  setQuery: (query: IFormReportInputs) => void;
  query: IFormReportInputs;
  filteredReport: () => ServiceReport[];
  saveAnexos: (data: PayloadAnexos) => Promise<boolean>;
  updateDateDelivery: (report: Partial<ReportType>) => Promise<boolean>;
  downloadControlReport: (
    id: ReportType["id"],
    refReport?: string
  ) => Promise<boolean>;
  fetchReportLid: (data: LidForm) => Promise<ResponseService | null>;
  loadingLid: boolean;
  downloadLIDReport: (
    id: ReportType["id"],
    refReport?: string
  ) => Promise<boolean>;
  sendEmailReport: (id: ReportType["id"]) => Promise<boolean>;
  fetchControlReportFiltered: (reportId: number, filters?: any) => Promise<ControlReportResult[] | null>;
  loadingControlReport: boolean;
};

export const useReport = create<UseReport>((set, get) => ({
  reportList: [],
  loadingControlReport: false,
  loadingLid: false,
  loading: false,
  loadingDownload: false,
  loadingSendEmail: false,
  loadingEdit: false,
  loadingSave: false,
  referenceList: [],
  query: {},
  setQuery: (query) => set({ query }),
  get: (id) => get().reportList.find((report) => report.id === id),
  filteredReport: () => filter(get().query, get().reportList),
  fetchAll: async () => {
    set({ loading: true });
    try {
      const endpoint = "/reports/filtered?estado=1"; // 1 = Activo
      const { data: response } = await getData(endpoint);

      if (response) {
        const cloneReportList = JSON.parse(JSON.stringify(response));
        const referenceSet = Array.from(
          new Set(
            cloneReportList.map((item: any) =>
              item.results_consecutive !== null
                ? `${item.ref_report}/${item.results_consecutive}`
                : item.ref_report
            )
          )
        ) as GenericOptionsType[];

        set({
          reportList: response.sort(
            (a: ServiceReport, b: ServiceReport) => b.id - a.id
          ),
          referenceList: referenceSet,
        });
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
    } finally {
      set({ loading: false });
    }
  },

  save: async (report: Partial<ReportType>, silent: boolean = false) => {
    set({ loadingSave: true });
    try {
      if (
        report.reportType === "results" &&
        report.anexos?.includes("anexo_2") &&
        (report.operation_limit_date === "No tiene fecha de Lim. Operación" ||
          report.operation_start_date === "No tiene fecha de In. Operación" ||
          !report.operation_limit_date ||
          !report.operation_start_date)
      ) {
        if (!silent) {
          errorAlert(
            "La instalación elegida no posee fechas límite para el Anexo II. Desmarque dicha opción o elija otra instalación."
          );
        }
        set({ loadingSave: false });
        return false;
      }

      const configReport = {
        results: { endpoint: "/reports/", mapping: samplingReport },
        control: {
          endpoint: "/control-reports/",
          mapping: samplingControlReport,
        },
        lid_csn: { endpoint: "/lid-csn/", mapping: samplingLIDReport },
      };
      const isNew = !report?.id;
      const endpoint = `${
        configReport[report.reportType as keyof typeof configReport].endpoint
      }${isNew ? "create" : report.id + "/edit"}`;

      const mappingData =
        configReport[report.reportType as keyof typeof configReport].mapping(
          report
        );

      const response = isNew
        ? await postData(endpoint, mappingData)
        : await putData(endpoint, mappingData);

      if (!response.success) {
        if (!silent) {
          errorAlert("Error al guardar el informe");
        }
        return false;
      }
      if (!silent) {
        successAlert("Informe guardado correctamente");
        get().fetchAll();
      }
      set({ loadingSave: false });

      return response.success;
    } catch (error) {
      if (!silent) {
        errorAlert("Error al guardar el informe");
      }
      set({ loadingSave: false });
      return false;
    }
  },

  remove: async (id: ReportType["id"]) => {
    set({ loadingSave: true });

    try {
      const endpoint = `/reports/${id}/edit`;

      await putData(endpoint, {
        estado: 0, // 0 = Inactivo
      });
      successAlert("Informe eliminado correctamente");
    } catch (error) {
      errorAlert("Error al eliminar el informe");
    } finally {
      set({ loadingSave: false });
      get().fetchAll();
    }
  },

  downloadReport: async (
    id: ReportType["id"],
    refReport: string = "",
    resultsConsecutive: number = 0,
    fechaEntrega: string = ""
  ) => {
    try {
      set({ loadingDownload: true });
      const endpoint = `/reports/${id}`;
      const { data: response } = await getDataFile(endpoint, {
        responseType: "blob",
      });

      // validar si es un contenido del blob o mensaje json
      const responseText = await response.text();
      try {
        const jsonResponse = JSON.parse(responseText);
        if (jsonResponse.message) {
          errorAlert(jsonResponse.message);
          return false;
        }
      } catch (e) {}

      if (!response) {
        return false;
      }

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      const FechaEntrega = fechaEntrega?.trim();
      let fileName = `${refReport}${
        resultsConsecutive ? `-${resultsConsecutive}` : ""
      }.pdf`;

      if (!FechaEntrega) {
        fileName = `${refReport}-draft.pdf`;
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successAlert("Informe descargado correctamente");

      return true;
    } catch (error) {
      errorAlert("Error al descargar el informe");
      return false;
    } finally {
      set({ loadingDownload: false });
    }
  },
  saveAnexos: async (data: PayloadAnexos) => {
    set({ loadingSave: true });
    const { id, ...mappingData } = data;

    if (!data.id) {
      return false;
    }

    try {
      const endpoint = `/reports/${id}/attachments`;
      const response = await putData(endpoint, mappingData);
      successAlert("Informe guardado correctamente");
      return response.success;
    } catch (error) {
      errorAlert("Error al guardar el informe");
      return false;
    } finally {
      set({ loadingSave: false });
      get().fetchAll();
    }
  },

  updateDateDelivery: async (
    report: Partial<ReportType> & {
      refReport?: string;
      resultsConsecutive?: string;
    }
  ) => {
    try {
      set({ loadingDownload: true });
      const { id, refReport, resultsConsecutive, ...mappingData } = report;
      const endpoint = `/reports/${report.id}/generate-reports`;
      const { data: response } = await putDataFile(endpoint, mappingData, {
        responseType: "blob",
      });

      // validar si es un contenido del blob o mensaje json
      const responseText = await response.text();
      try {
        const jsonResponse = JSON.parse(responseText);

        if (jsonResponse.message) {
          errorAlert(jsonResponse.message);
          return false;
        }
      } catch (e) {}

      if (!response) {
        return false;
      }

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      let fileName = `Informe_${report.id}.pdf`;

      if (refReport) {
        fileName = `${refReport}${
          resultsConsecutive ? `-${resultsConsecutive}` : ""
        }.pdf`.replace("-null", "");
      }
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successAlert("Informe descargado correctamente");
      get().fetchAll();
      return true;
    } catch (error) {
      errorAlert("Error al descargar el informe");
      return false;
    } finally {
      set({ loadingDownload: false });
    }
  },

  downloadControlReport: async (
    id: ReportType["id"],
    refReport: string = ""
  ) => {
    try {
      set({ loadingDownload: true });
      const endpoint = `/control-reports/${id}/get-file`;
      const { data: response } = await getDataFile(endpoint, {
        responseType: "blob",
      });

      if (!response) {
        return false;
      }

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      // link.setAttribute("download", `Informe_${id}.pdf`);
      link.setAttribute("download", `Informe_${refReport}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      successAlert("Informe descargado correctamente");

      return true;
    } catch (error) {
      errorAlert("Error al descargar el informe");
      return false;
    } finally {
      set({ loadingDownload: false });
    }
  },

  downloadLIDReport: async (id: ReportType["id"], refReport: string = "") => {
    try {
      set({ loadingDownload: true });
      const endpoint = `/reports/${id}/generate-reports`;
      const { data: response } = await putDataFile(
        endpoint,
        { fecha_entrega: dayjs().format("YYYY-MM-DD") },
        {
          responseType: "blob",
        }
      );

      if (!response) {
        return false;
      }

      // validar si es un contenido del blob o mensaje json
      const responseText = await response.text();
      try {
        const jsonResponse = JSON.parse(responseText);

        if (jsonResponse.message) {
          errorAlert(jsonResponse.message);
          return false;
        }
      } catch (e) {}

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      //link.setAttribute("download", `Report_lid_${id}.pdf`);
      link.setAttribute("download", `Report_lid_${refReport}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      successAlert("Informe descargado correctamente");

      return true;
    } catch (error) {
      errorAlert("Error al descargar el informe");
      return false;
    } finally {
      set({ loadingDownload: false });
    }
  },
  fetchReportLid: async (data: LidForm): Promise<ResponseService | null> => {
    set({ loadingLid: true });
    try {
      const endpoint = `/lid-csn/filtered?installation=${
        data.installation_lid.id
      }&evo_start_date=${dayjs(data.evo_start_date_lid).format(
        "YYYY-MM-DD"
      )}&evo_end_date=${dayjs(data.evo_end_date_lid).format(
        "YYYY-MM-DD"
      )}&created_by=${data.created_by.id}`;

      const { data: response } = await getData(endpoint);

      if (response) {
        return response as ResponseService;
      } else {
        errorAlert("No se encontraron datos para el filtro proporcionado.");
        return null;
      }
    } catch (error) {
      console.error("Error al obtener el reporte LID:", error);
      errorAlert("Error al obtener el reporte LID");
      return null;
    } finally {
      set({ loadingLid: false });
    }
  },
  sendEmailReport: async (id: ReportType["id"]) => {
    try {
      set({ loadingSendEmail: true });
      const endpoint = `/reports/send-to-email/${id}`;
      const response = await postData(endpoint, {});

      if (response.success) {
        successAlert("Correo enviado correctamente");
        return true;
      } else {
        errorAlert("Error al enviar el correo");
        return false;
      }
    } catch (error) {
      errorAlert("Error al enviar el correo");
      return false;
    } finally {
      set({ loadingSendEmail: false });
    }
  },
  
  fetchControlReportFiltered: async (reportId: number, filters?: any): Promise<ControlReportResult[] | null> => {
    set({ loadingControlReport: true });
    try {
      const endpoint = `/control-reports/${reportId}/filtered`;
      // Construir query params con los filtros
      const params = new URLSearchParams();
      if (filters) {
        if (filters.activity_comparator && filters.activity_value !== undefined) {
          params.append('activity_comparator', filters.activity_comparator);
          params.append('activity_value', filters.activity_value.toString());
          if (filters.activity_value_max !== undefined && filters.activity_comparator === 'between') {
            params.append('activity_value_max', filters.activity_value_max.toString());
          }
        }
        if (filters.uncertainty_comparator && filters.uncertainty_value !== undefined) {
          params.append('uncertainty_comparator', filters.uncertainty_comparator);
          params.append('uncertainty_value', filters.uncertainty_value.toString());
          if (filters.uncertainty_value_max !== undefined && filters.uncertainty_comparator === 'between') {
            params.append('uncertainty_value_max', filters.uncertainty_value_max.toString());
          }
        }
        if (filters.lid_comparator && filters.lid_value !== undefined) {
          params.append('lid_comparator', filters.lid_comparator);
          params.append('lid_value', filters.lid_value.toString());
          if (filters.lid_value_max !== undefined && filters.lid_comparator === 'between') {
            params.append('lid_value_max', filters.lid_value_max.toString());
          }
        }
      }
      
      const fullEndpoint = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
      const { data: response } = await getData(fullEndpoint);

      if (response && response.results) {
        return response.results as ControlReportResult[];
      } else {
        errorAlert("No se encontraron datos para el filtro proporcionado.");
        return null;
      }
    } catch (error) {
      console.error("Error al obtener el reporte de control:", error);
      errorAlert("Error al obtener el reporte de control");
      return null;
    } finally {
      set({ loadingControlReport: false });
    }
  },
}));
